import * as THREE from 'three';
import type { OcclusionSystem } from '@/fx/OcclusionSystem';
import type { LightPool } from '@/fx/lighting';
import type { TrafficController, TrafficChunkHandles } from '@/gameplay/Traffic';
import { loadLevel, type LoadedLevel } from '@/world/LevelLoader';
import type { CityChunk } from '@/world/CityMap';
import { CITY_CHUNKS, chunkAt } from '@/world/CityMap';
import type { CollisionWorld } from '@/world/collision';

export interface LoadedChunk {
  readonly id: string;
  readonly root: THREE.Group;
  readonly level: LoadedLevel;
  readonly traffic: TrafficChunkHandles;
}

export interface WorldStreamerStats {
  readonly loadedChunkIds: readonly string[];
  readonly activeChunkIds: readonly string[];
  readonly loadedBuildings: number;
  readonly loadedCollision: number;
  readonly loadedLights: number;
  readonly activeTraffic: number;
}

export function disposeObjectGeometries(root: THREE.Object3D): number {
  const disposed = new Set<THREE.BufferGeometry>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    if (disposed.has(object.geometry)) return;
    object.geometry.dispose();
    disposed.add(object.geometry);
  });
  return disposed.size;
}

const PRELOAD_NEIGHBOR_DEPTH = 1;
const UNLOAD_NEIGHBOR_DEPTH = 2;
const VEHICLE_LOOKAHEAD_SECONDS = 3.2;

export class WorldStreamer {
  readonly #chunks: readonly CityChunk[];
  readonly #byId = new Map<string, CityChunk>();
  readonly #loaded = new Map<string, LoadedChunk>();
  readonly #scene: THREE.Scene;
  readonly #collision: CollisionWorld;
  readonly #lights: LightPool;
  readonly #traffic: TrafficController;
  readonly #occlusion: OcclusionSystem | undefined;
  readonly #active = new Set<string>();

  constructor(config: {
    readonly scene: THREE.Scene;
    readonly collision: CollisionWorld;
    readonly lights: LightPool;
    readonly traffic: TrafficController;
    readonly occlusion?: OcclusionSystem;
    readonly chunks?: readonly CityChunk[];
  }) {
    this.#scene = config.scene;
    this.#collision = config.collision;
    this.#lights = config.lights;
    this.#traffic = config.traffic;
    this.#occlusion = config.occlusion;
    this.#chunks = config.chunks ?? CITY_CHUNKS;
    for (const chunk of this.#chunks) this.#byId.set(chunk.id, chunk);
  }

  update(position: THREE.Vector2, velocity: THREE.Vector2 = new THREE.Vector2()): void {
    const anchor = this.#anchorFor(position);
    if (!anchor) return;

    const desired = this.#neighborSet(anchor.id, PRELOAD_NEIGHBOR_DEPTH);
    const speed = velocity.length();
    if (speed > 0.5) {
      const lookahead = position.clone().addScaledVector(velocity.clone().normalize(), speed * VEHICLE_LOOKAHEAD_SECONDS);
      const ahead = chunkAt(lookahead, this.#chunks);
      if (ahead) {
        for (const id of this.#neighborSet(ahead.id, PRELOAD_NEIGHBOR_DEPTH)) desired.add(id);
      }
    }

    for (const id of desired) this.#load(id);

    const keep = this.#neighborSet(anchor.id, UNLOAD_NEIGHBOR_DEPTH);
    for (const id of [...this.#loaded.keys()]) {
      if (!keep.has(id) && !desired.has(id)) this.#unload(id);
    }

    this.#active.clear();
    for (const id of desired) this.#active.add(id);
  }

  loadInitial(position: THREE.Vector2): void {
    this.update(position);
  }

  get loadedChunkIds(): readonly string[] {
    return [...this.#loaded.keys()].sort();
  }

  get activeChunkIds(): readonly string[] {
    return [...this.#active].sort();
  }

  chunk(id: string): CityChunk | undefined {
    return this.#byId.get(id);
  }

  stats(): WorldStreamerStats {
    let loadedBuildings = 0;
    let loadedCollision = 0;
    let loadedLights = 0;
    for (const chunk of this.#loaded.values()) {
      loadedBuildings += chunk.level.buildingCount;
      loadedCollision += chunk.level.collisionHandles.length;
      loadedLights += chunk.level.lightHandles.length;
    }
    return {
      loadedChunkIds: this.loadedChunkIds,
      activeChunkIds: this.activeChunkIds,
      loadedBuildings,
      loadedCollision,
      loadedLights,
      activeTraffic: this.#traffic.activeCount + this.#traffic.parkedCount,
    };
  }

  /**
   * Welke chunk is "hier"?
   *
   * Stond je buiten elke chunk — op het kadedek voorbij de grondrand, of net over een
   * districtrand heen — dan gaf `chunkAt` null en stopte de streamer er zwijgend mee: geen
   * laden, geen lossen, en een lege districtnaam in de HUD. Buiten de bounds vallen we terug
   * op de dichtstbijzijnde chunk, zodat de wereld blijft meebewegen.
   */
  #anchorFor(position: THREE.Vector2): CityChunk | null {
    const inside = chunkAt(position, this.#chunks);
    if (inside) return inside;

    let nearest: CityChunk | null = null;
    let nearestDistance = Infinity;
    for (const chunk of this.#chunks) {
      const dx = Math.max(0, Math.abs(position.x - chunk.bounds.x) - chunk.bounds.halfX);
      const dz = Math.max(0, Math.abs(position.y - chunk.bounds.z) - chunk.bounds.halfZ);
      const distance = dx * dx + dz * dz;
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = chunk;
      }
    }
    return nearest;
  }

  #load(id: string): void {
    if (this.#loaded.has(id)) return;
    const chunk = this.#byId.get(id);
    if (!chunk) return;
    const level = loadLevel(this.#scene, this.#collision, this.#lights, chunk.levelData);
    this.#occlusion?.register(level.root);
    const traffic = this.#traffic.addChunkTraffic(id, {
      routes: chunk.levelData.trafficRoutes ?? [],
      parkedVehicles: chunk.levelData.parkedVehicles ?? [],
    });
    this.#loaded.set(id, { id, root: level.root, level, traffic });
  }

  #unload(id: string): void {
    const loaded = this.#loaded.get(id);
    if (!loaded) return;
    this.#occlusion?.unregister(loaded.root);
    this.#scene.remove(loaded.root);
    for (const handle of loaded.level.collisionHandles) this.#collision.remove(handle);
    for (const light of loaded.level.lightHandles) this.#lights.remove(light);
    this.#traffic.removeChunkTraffic(id);
    disposeObjectGeometries(loaded.root);
    this.#loaded.delete(id);
  }

  #neighborSet(id: string, depth: number): Set<string> {
    const seen = new Set<string>([id]);
    let frontier = [id];
    for (let step = 0; step < depth; step++) {
      const next: string[] = [];
      for (const current of frontier) {
        const chunk = this.#byId.get(current);
        if (!chunk) continue;
        for (const neighbor of chunk.neighbors) {
          if (seen.has(neighbor)) continue;
          seen.add(neighbor);
          next.push(neighbor);
        }
      }
      frontier = next;
    }
    return seen;
  }
}
